import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef, ElementRef,
  HostListener,
  Injector,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {
  CloseEvent, ConfirmEvent, LoadingModalEvent, ModalEvent, ModalService
} from './service';
import { AbstractModalComponent } from './abstractmodal';
import { Tools } from 'linkurious-shared';
import {AbstractConfirmComponent} from "./abstractconfirm";
import {ModalConfirmComponent} from "./confirm/component";

const BASE_HEIGHT = 125;

@Component({
  selector: 'lk-modal',
  template: `<div class="modal__curtain"
     [ngClass]="{active: loading || opened}">
  <label class="progress_spinner" [class.active]="longLoading">
    <span class="spinner"></span>
  </label>
</div>
<div #curtain class="modal__wrapper"
     [ngClass]="{active: opened}">
  <div #modal class="modal__content"
       [ngClass]="size">
    <div class="modal__header">
      <div class="modal__title"><ng-template #header></ng-template></div>
      <icon class="modal__close"
            name='close'
            stroke size="23"
            (click)="close()"></icon>
    </div>
    <div class="modal__body"
         [ngStyle]="modalHeight">
      <ng-template #body></ng-template>
    </div>
    <div class="modal__footer">
      <ng-template #footer></ng-template>
    </div>
  </div>
</div>
`,
  styles: [`.modal__curtain{background:rgba(0,0,0,.65);height:100%;left:0;opacity:0;position:fixed;top:0;transition:all ease-in-out .3s;visibility:hidden;width:100%;z-index:9997;display:flex;align-items:center;justify-content:space-around}.modal__curtain.active{opacity:1;visibility:visible}.modal__wrapper{align-items:center;display:flex;justify-content:center;height:100%;left:0;opacity:0;position:fixed;-webkit-transform:scale(.8);transform:scale(.8);top:0;transition:all ease-in-out .3s;visibility:hidden;width:100%;z-index:9998}.modal__wrapper.active{opacity:1;-webkit-transform:scale(1);transform:scale(1);visibility:visible}.modal__content{background:#fff;-o-box-shadow:0 6px 12px rgba(0,0,0,.175);-ms-box-shadow:0 6px 12px rgba(0,0,0,.175);box-shadow:0 6px 12px rgba(0,0,0,.175)}.modal__content.small{width:500px}.modal__content.large{width:850px}.modal__content.fullscreen{width:100%}.modal__content .modal__header{align-items:center;box-sizing:border-box;color:#363636;display:flex;font-size:14px;height:65px;padding:20px;text-transform:uppercase;width:100%}.modal__content .modal__header .modal__title{flex:1}.modal__content .modal__header .modal__close{flex:0 0 25px;cursor:pointer}.modal__content .modal__body{overflow-y:auto;position:relative}.progress_spinner{z-index:2;visibility:hidden;opacity:0;transition:all ease-in-out .5s}.progress_spinner.active{visibility:visible;opacity:1}.progress_spinner .spinner{border:4px solid rgba(255,255,255,.2);border-top:4px solid rgba(255,255,255,.6);border-radius:50%;display:block;width:40px;height:40px;-webkit-animation:2s linear infinite spin;animation:2s linear infinite spin;margin:0!important}@-webkit-keyframes spin{0%{-webkit-transform:rotate(0);transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}@keyframes spin{0%{-webkit-transform:rotate(0);transform:rotate(0)}100%{-webkit-transform:rotate(360deg);transform:rotate(360deg)}}.progress_spinner .hidden{display:none}`]
})
export class ModalComponent implements OnInit {

  @ViewChild('header', {read: ViewContainerRef}) private _header: ViewContainerRef;
  @ViewChild('body', {read: ViewContainerRef}) private _body: ViewContainerRef;
  @ViewChild('footer', {read: ViewContainerRef}) private _footer: ViewContainerRef;
  @ViewChild('modal') private _modalWrapper;
  @ViewChild('curtain') private _curtain: ElementRef;

  private _childComponentRef: ComponentRef<AbstractModalComponent|AbstractConfirmComponent>;
  public implicitDismiss: boolean;
  public size: 'small'|'large'|'fullscreen';
  private _absoluteHeight: number;
  public modalHeight: {
    height?: string;
    maxHeight?: string;
    width?: string;
  };
  public loading: boolean = false;
  public loadingTimeout: any;
  public longLoading: boolean = false;
  public opened: boolean = false;

  constructor(
    private _modal: ModalService,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _injector: Injector,
    private _detectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._modal.event$
      .subscribe(e => {
        if ( e instanceof CloseEvent ) {
          this.forceClose();
        } else if ( e instanceof LoadingModalEvent ) {
          this.openCurtain();
        } else {
          this.open(e);
        }
      });
  }

  /**
   * Close the modal on press 'escape'
   */
  @HostListener('document:keyup', ['$event'])
  public onKeyUp(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.implicitDismiss === true ) {
      e.preventDefault();
      e.stopPropagation();
      this.forceClose();
    }
  }

  /**
   * Close the modal on click outside
   */
  @HostListener('document:click', ['$event'])
  public onClickOutside(e: MouseEvent) {
    if (
      this.size !== 'fullscreen' &&
      e.target === this._curtain.nativeElement &&
      this.implicitDismiss === true
    ) {
      e.preventDefault();
      e.stopPropagation();
      this.forceClose();
    }
  }

  /**
   * Resize the modal depending of the configuration and the screen size
   */
  @HostListener('window:resize')
  public resize(): void {
    let viewportWidth = document.documentElement.clientWidth;
    let viewportHeight = document.documentElement.clientHeight;
    if ( this.size === 'fullscreen' ) {
      let HeightDiff = ( this._childComponentRef.instance.actions.length > 0 )
        ? BASE_HEIGHT
        : 65;
      this.modalHeight = {
        height: Tools.numberToPixels(viewportHeight - HeightDiff),
        width: Tools.numberToPixels(viewportWidth)
      }
    } else {
      if ( this._absoluteHeight ) {
        let height = ( viewportHeight > this._absoluteHeight ) ? this._absoluteHeight : viewportHeight;
        this.modalHeight = { height: Tools.numberToPixels(height - BASE_HEIGHT) };
      } else {
        let height = ( viewportHeight > 300 ) ? viewportHeight * .8 : viewportHeight;
        this.modalHeight = { maxHeight: Tools.numberToPixels(height - BASE_HEIGHT) };
      }
      let currentModalWidth = ( this.size === 'small' ) ? 500 : 850;
      if ( viewportWidth < currentModalWidth ) {
        this.modalHeight.width = Tools.numberToPixels(viewportWidth);
      }
    }
    this._detectorRef.markForCheck();
  }

  /**
   * Close the modal
   */
  public close() {
    if ( this._childComponentRef && this._childComponentRef.instance.onDismiss ) {
      this._childComponentRef.instance.onDismiss();
    } else {
      this.forceClose();
    }
  }

  /**
   * Force close modal
   */
  private forceClose() {
    this.opened = false;
    this.loading = false;
    clearTimeout(this.loadingTimeout);
    this.longLoading = false;
    setTimeout(() => this.destroyCurrentChild(), 300);
  }

  /**
   * Destroy the current component injected in the modal
   */
  private destroyCurrentChild(): void {
    if ( this._childComponentRef ) {
      if ( this._childComponentRef.instance.ngOnDestroy ) {
        this._childComponentRef.instance.ngOnDestroy();
      }
      this._childComponentRef.destroy();
    }
    this._header.clear();
    this._body.clear();
    this._footer.clear();
    this.implicitDismiss = true;
    this.modalHeight = undefined;
    this._childComponentRef = undefined;
  }

  /**
   * Show the modal curtain
   */
  private openCurtain(): void {
    clearTimeout(this.loadingTimeout);
    this.longLoading = false;
    this.loading = true;
    this.loadingTimeout = setTimeout(() => {
      this.longLoading = true;
      this._detectorRef.markForCheck();
    }, 500);
  }

  /**
   * Open a modal
   */
  private open(event: ModalEvent|ConfirmEvent): void {
    let isConfirm = false;
    let componentToLoad: Type<AbstractModalComponent>;
    if ( this._childComponentRef ) {
      this.destroyCurrentChild();
    }
    if ( typeof event.content === 'string' ) {
      isConfirm = true;
      componentToLoad = ModalConfirmComponent as Type<AbstractConfirmComponent>;
    } else {
      componentToLoad = event.content as Type<AbstractModalComponent>;
    }
    // initialize modal component
    try {
      this._childComponentRef = this._componentFactoryResolver
        .resolveComponentFactory(componentToLoad)
        .create(this._injector);
    } catch (e) {
      this.forceClose();
      this._detectorRef.markForCheck();
      throw new Error(e);
    }

    this._header.createEmbeddedView(this._childComponentRef.instance.modalHeader);
    this._body.createEmbeddedView(this._childComponentRef.instance.modalBody);
    if ( isConfirm ) {
      let confirmEvent = event as ConfirmEvent;
      let confirmComponent = this._childComponentRef as ComponentRef<AbstractConfirmComponent>;
      confirmComponent.instance.title = confirmEvent.title;
      confirmComponent.instance.content = confirmEvent.content;
    }
    if ( this._childComponentRef.instance.modalActions ) {
      this._footer.createEmbeddedView(this._childComponentRef.instance.modalActions);
    }
    // add actions
    if ( event.onValidate ) {
      this._childComponentRef.instance.actions.splice(
        this._childComponentRef.instance.actions.length,
        0,
        event.onValidate
      );
      this._childComponentRef.instance.onValidate = event.onValidate.onClick;
      event.onValidate.onClick = () => this._childComponentRef.instance.triggerValidation();
      if ( event.onValidate.hidden ) {
        this._childComponentRef.instance.onHiddenValidation = event.onValidate.hidden;
        event.onValidate.hidden = () => this._childComponentRef.instance.triggerHideValidation();
      }
      if ( event.onValidate.disable ) {
        this._childComponentRef.instance.onDisableValidation = event.onValidate.disable;
        event.onValidate.disable = () => this._childComponentRef.instance.triggerDisableValidation();
      }
    }
    if ( event.onDismiss ) {
      this._childComponentRef.instance.actions.splice(0, 0, event.onDismiss);
      this._childComponentRef.instance.onDismiss = event.onDismiss.onClick;
      event.onDismiss.onClick = () => {
        this.close();
        return this._childComponentRef.instance.triggerCancellation();
      };
      if ( event.onDismiss.hidden ) {
        this._childComponentRef.instance.onHiddenDismiss = event.onDismiss.hidden;
        event.onDismiss.hidden = () => this._childComponentRef.instance.triggerHideDismiss();
      }
      if ( event.onDismiss.disable ) {
        this._childComponentRef.instance.onDisableDismiss = event.onDismiss.disable;
        event.onDismiss.disable = () => this._childComponentRef.instance.triggerDisableDismiss();
      }
    }
    // set values to the modal component
    Object.keys(event.resolve).forEach(instanceProperty => {
      this._childComponentRef.instance[instanceProperty] = event.resolve[instanceProperty];
    });
    if ( this._childComponentRef.instance.ngOnInit ) {
      this._childComponentRef.instance.ngOnInit();
    }
    this.implicitDismiss = event.implicitDismiss;

    // set modal size and absolute height if provided
    this.size = event.size;
    this._absoluteHeight = event.height;
    this.resize();
    clearTimeout(this.loadingTimeout);
    this.longLoading = false;
    this.loading = false;
    this.opened = true;
  }
}
